import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface CreatePolicyModalProps {
  onClose: (created?: boolean) => void;
}
const policyContent = `# My Company's Credit Policy

## Overview
This credit policy outlines how our organization evaluates and extends credit to new customers. We built this policy using Credit Pulse's default template as a starting point and customized it to fit our business needs.

## Credit Evaluation Criteria

### New Customer Assessment

**1. Company Verification**
- We verify the applicant's business registration and legal status
- We confirm their business address and contact information
- We validate their business operations and industry classification

**2. Financial Review**
- We analyze available financial statements
- We pull credit reports from major credit bureaus
- We review payment history and check trade references

**3. Risk Assessment**
- We evaluate credit risk scores from our data providers
- We review any relevant business news and public records
- We factor in industry-specific risk considerations

## Credit Limits

We determine initial credit limits based on:
- The applicant's financial strength
- Their payment history with other vendors
- Feedback from trade references
- Industry benchmarks and standards

## Payment Terms

Our standard payment terms are Net 30 days from the invoice date.

## Review Process

Our credit team will review all applications within 2-3 business days. We may request additional documentation during the review process.

---

*Created with Credit Pulse — Automated Credit Decisioning*`;
function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let key = 0;
  const flushList = () => {
    if (listItems.length > 0 && listType) {
      if (listType === 'ul') {
        elements.push(<ul key={key++} className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-3" data-id="element-487" data-mp="22qq8frw5hrx7">
            {listItems}
          </ul>);
      } else {
        elements.push(<ol key={key++} className="list-decimal list-inside space-y-1 text-gray-700 ml-4 mb-3" data-id="element-488" data-mp="2ci30ajhy8i38">
            {listItems}
          </ol>);
      }
      listItems = [];
      listType = null;
    }
  };
  const formatInline = (str: string) => {
    const parts: React.ReactNode[] = [];
    let remaining = str;
    let partKey = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      const italicMatch = remaining.match(/\*(.+?)\*/);
      if (boldMatch && boldMatch.index !== undefined && (!italicMatch || boldMatch.index <= (italicMatch.index ?? Infinity))) {
        if (boldMatch.index > 0) {
          parts.push(<span key={partKey++} data-id="element-489" data-mp="399oho2l2o7fr">{remaining.slice(0, boldMatch.index)}</span>);
        }
        parts.push(<strong key={partKey++} className="font-semibold text-gray-900" data-id="element-490" data-mp="tj55427xdqey">
            {boldMatch[1]}
          </strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      } else if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          parts.push(<span key={partKey++} data-id="element-491" data-mp="eus5mv9mppfp">
              {remaining.slice(0, italicMatch.index)}
            </span>);
        }
        parts.push(<em key={partKey++} className="italic text-gray-500" data-id="element-492" data-mp="5tarvjjp1zu6">
            {italicMatch[1]}
          </em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      } else {
        parts.push(<span key={partKey++} data-id="element-493" data-mp="207v7hht5r56p">{remaining}</span>);
        remaining = '';
      }
    }
    return parts;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('### ')) {
      flushList();
      elements.push(<h3 key={key++} className="text-base font-semibold text-gray-800 mt-4 mb-2" data-id="element-494" data-mp="ry4e1cx44o4g">
          {line.slice(4)}
        </h3>);
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(<h2 key={key++} className="text-lg font-bold text-gray-900 mt-5 mb-2" data-id="element-495" data-mp="nv856dxdk6dp">
          {line.slice(3)}
        </h2>);
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(<h1 key={key++} className="text-xl font-bold text-gray-900 mb-3" data-id="element-496" data-mp="dy3i8bvpg6vu">
          {line.slice(2)}
        </h1>);
    } else if (line.startsWith('---')) {
      flushList();
      elements.push(<hr key={key++} className="border-gray-200 my-4" data-id="element-497" data-mp="478suyn5wr66" />);
    } else if (line.match(/^\d+\.\s/)) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      const content = line.replace(/^\d+\.\s/, '');
      listItems.push(<li key={key++} className="text-gray-700" data-id="element-498" data-mp="3rborfmyi98i5">
          {formatInline(content)}
        </li>);
    } else if (line.match(/^\s*-\s/)) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      const content = line.replace(/^\s*-\s/, '');
      listItems.push(<li key={key++} className="text-gray-700" data-id="element-499" data-mp="3hmrgc55ovzb7">
          {formatInline(content)}
        </li>);
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      elements.push(<p key={key++} className="text-gray-700 mb-2" data-id="element-500" data-mp="3qn65jflrxyjz">
          {formatInline(line)}
        </p>);
    }
  }
  flushList();
  return elements;
}
export function CreatePolicyModal({
  onClose
}: CreatePolicyModalProps) {
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [displayedContent, setDisplayedContent] = useState('');
  const [policyName] = useState("My Company's Credit Policy");
  const [versionDescription] = useState('Version 1');
  const [useDefaultPolicy, setUseDefaultPolicy] = useState(true);
  useEffect(() => {
    if (step === 2 && isGenerating) {
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex <= policyContent.length) {
          setDisplayedContent(policyContent.slice(0, currentIndex));
          currentIndex += 2;
        } else {
          setIsGenerating(false);
          clearInterval(interval);
        }
      }, 15);
      return () => clearInterval(interval);
    }
  }, [step, isGenerating]);
  const handleNext = () => {
    if (step === 1) {
      setStep(2);
      setIsGenerating(true);
    }
  };
  const handleCreate = () => {
    onClose(true);
  };
  const renderedContent = useMemo(() => renderMarkdown(displayedContent), [displayedContent]);
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-501" data-mp="1dxn3uqha20nx">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-502" data-mp="3aum74vdzp56v">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-503" data-mp="1o4fwlilnr1xx">
          <div data-id="element-504" data-mp="1j64k103zadc0">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-505" data-mp="1j6o3wmd6vbso">
              Create New Policy
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-506" data-mp="2fszrw75vgy8o">
              Create a new credit policy from scratch
            </p>
          </div>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600" data-id="element-507" data-mp="26tw7mzuyycsl">
            <X className="w-6 h-6" data-id="element-508" data-mp="21ut4jkyjugn9" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-509" data-mp="vgtuyfvzq7dp">
          <div className="flex items-center" data-id="element-510" data-mp="qfmkua6dkw3v">
            <div className="flex items-center" data-id="element-511" data-mp="qeuo2w11ksc0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-white'}`} data-id="element-512" data-mp="2qhvxy6olxer1">
                1
              </div>
              <span className={`ml-3 font-medium ${step === 1 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-513" data-mp="2glsiy08nplmo">
                Policy Details
              </span>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-indigo-600' : 'bg-gray-300'}`} data-id="element-514" data-mp="399exg3jfaaob" />
            <div className="flex items-center" data-id="element-515" data-mp="3e9ua0svvre58">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${step === 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-white'}`} data-id="element-516" data-mp="2zi26de5g7ojc">
                2
              </div>
              <span className={`ml-3 font-medium ${step === 2 ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-517" data-mp="3s5o8sj2xwn4m">
                Policy Content
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-6" data-id="element-518" data-mp="1gl78m1ywps9t">
          <AnimatePresence mode="wait" data-id="element-519" data-mp="1gil0o183tar2">
            {step === 1 ? <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-520" data-mp="1v8sda0bfjzju">
                <div data-id="element-521" data-mp="1bmnn22o86ihz">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-522" data-mp="17jqy7u83dq5l">
                    Policy Name <span className="text-red-500" data-id="element-523" data-mp="17m3mep4g0wr3">*</span>
                  </label>
                  <input type="text" value={policyName} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50" data-id="element-524" data-mp="2n2knan1ikoui" />
                </div>

                <div data-id="element-525" data-mp="124cb1jnfpl85">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-526" data-mp="123szqlsahv33">
                    Policy Type <span className="text-red-500" data-id="element-527" data-mp="xazyi7w154vr">*</span>
                  </label>
                  <div className="flex gap-6" data-id="element-528" data-mp="sconhtu00oby">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-529" data-mp="nede1k7m8fu9">
                      <input type="radio" name="policyType" checked readOnly className="w-5 h-5 text-indigo-600" data-id="element-530" data-mp="ncadi68u74ba" />
                      <span className="text-gray-900" data-id="element-531" data-mp="9fdlw2kw9itc">
                        New Customer Policies
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-532" data-mp="28pdhaybxeu4x">
                      <input type="radio" name="policyType" className="w-5 h-5 text-indigo-600" data-id="element-533" data-mp="23teq72z7qy0t" />
                      <span className="text-gray-900" data-id="element-534" data-mp="2g9ec59kbfa6j">
                        Existing Customer Policies
                      </span>
                    </label>
                  </div>
                </div>

                <div data-id="element-535" data-mp="2q092ap0omtx9">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-536" data-mp="1sd9zdwjecpqx">
                    Version Description
                  </label>
                  <textarea value={versionDescription} readOnly rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 resize-none" data-id="element-537" data-mp="1xd62ltk9sd5f" />
                  <p className="text-sm text-gray-500 mt-1" data-id="element-538" data-mp="3dqwjy20egxx0">
                    9/1000 characters
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4" data-id="element-539" data-mp="3hqwwfh3xuz1f">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-540" data-mp="1imot0zaw4upj">
                    <input type="checkbox" checked={useDefaultPolicy} onChange={e => setUseDefaultPolicy(e.target.checked)} className="w-5 h-5 text-indigo-600 rounded mt-0.5" data-id="element-541" data-mp="1dodg2fpau433" />
                    <div data-id="element-542" data-mp="1mn8qy53ag2cq">
                      <span className="font-medium text-gray-900" data-id="element-543" data-mp="yyz9ffd3y0re">
                        Start from Credit Pulse's default policy
                      </span>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-544" data-mp="uvsvdljr75ad">
                        No template available for this policy type
                      </p>
                    </div>
                  </label>
                </div>
              </motion.div> : <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-545" data-mp="g7es59ifdeta">
                <div className="bg-white rounded-lg p-6 border border-gray-200 min-h-[400px]" data-id="element-546" data-mp="2dxcwxz4xm6fa">
                  {renderedContent}
                  {isGenerating && <motion.span animate={{
                opacity: [1, 0]
              }} transition={{
                duration: 0.5,
                repeat: Infinity
              }} className="inline-block w-2 h-5 bg-indigo-600 ml-0.5 align-middle" data-id="element-547" data-mp="36l91tr4nn4n5" />}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3" data-id="element-548" data-mp="1q7948mta7r32">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium" data-id="element-549" data-mp="25gw1ocv1qfqn">
            Cancel
          </button>
          {step === 1 ? <div className="relative" data-id="element-550" data-mp="1wp3vied21srv">
              <motion.div animate={{
            x: [0, -8, 0]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute right-full mr-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 whitespace-nowrap" data-id="element-551" data-mp="1wly4gtkv8gti">
                <span className="bg-gray-900 text-white text-sm font-medium px-3 py-1.5 rounded-lg shadow-lg" data-id="element-552" data-mp="2fgmzanewlauy">
                  Click Next
                </span>
                <ArrowRight className="w-5 h-5 text-gray-900 -ml-1" data-id="element-553" data-mp="140g8ad8qwtbo" />
              </motion.div>
              <button onClick={handleNext} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium relative z-10" data-id="element-554" data-mp="yzsh9rkiochg">
                Next
              </button>
            </div> : <button onClick={handleCreate} disabled={isGenerating} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-555" data-mp="yu9pk00jwmw4">
              Create
            </button>}
        </div>
      </motion.div>
    </div>;
}